import { useState } from "react";
import { CardSelected } from "../type";
import { BatchPriceCardsRequestType } from "magic-shared-types";
import {
  useBatchPriceCardsCardTraderMutation,
  useBatchPriceCardsCardMarketMutation,
  useBatchPriceCardsCardMarketTrendMutation,
} from "../services/myServer/priceCards";
import { selectGame } from "../store/slices/global";
import { useSelector } from "react-redux";

type UseBatchPriceCardsPayload = {
  cardsBoardFiltered: CardSelected[];
  cardsBoard: CardSelected[];
  onUpdateBoard: (cards: CardSelected[]) => void;
  strategyId: number;
  applyOnlyIfGreater: boolean;
  applyOnlyIfSmaller: boolean;
  isEdit?: boolean;
  maximumPercentageDecreasePrice?: number;
  maximumPercentageIncreasePrice?: number;
};

type UseBatchPriceCardsResult = {
  priceCards: ({
    cardsBoardFiltered,
    cardsBoard,
    onUpdateBoard,
  }: UseBatchPriceCardsPayload) => Promise<void>;
  isApiLoading: boolean;
};

type UseBatchPriceCardsPayloadTrend = {
  cardsBoardFiltered: CardSelected[];
  cardsBoard: CardSelected[];
  onUpdateBoard: (cards: CardSelected[]) => void;
  selectedAvgStrategy: string;
  percentage: number;
  applyOnlyIfGreater: boolean;
  applyOnlyIfSmaller: boolean;
  minPrice: number;
  isEdit?: boolean;
  maximumPercentageDecreasePrice?: number;
  maximumPercentageIncreasePrice?: number;
};

type UseBatchPriceCardsResultTrend = {
  priceCards: ({
    cardsBoardFiltered,
    cardsBoard,
    onUpdateBoard,
    selectedAvgStrategy,
    percentage,
    applyOnlyIfGreater,
    applyOnlyIfSmaller,
  }: UseBatchPriceCardsPayloadTrend) => Promise<void>;
  isApiLoading: boolean;
};

export const useBatchPriceCardsCardTrader = (): UseBatchPriceCardsResult => {
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [batchPriceCards] = useBatchPriceCardsCardTraderMutation();
  const game = useSelector(selectGame);

  const splitIntoBatches = (
    array: CardSelected[],
    size: number
  ): BatchPriceCardsRequestType[][] => {
    const batches = [];
    for (let i = 0; i < array.length; i += size) {
      const batch = array.slice(i, i + size).map((card) => ({
        price: card.price ?? 0,
        blue_print_id: card.id,
        condition: card?.condition,
        is_foil: card.finish === "foil" || false,
        language: card.language,
        is_signed: card?.signed || false,
        is_altered: false,
        uid: card.uid,
        game: game.card_trader_game,
        is_first_edition: card?.isFirstEdition ?? false,
        is_reverse: card?.isReversed ?? false,
      }));
      batches.push(batch);
    }
    return batches;
  };

  const priceCards = async ({
    cardsBoard,
    onUpdateBoard,
    cardsBoardFiltered,
    strategyId,
    applyOnlyIfGreater,
    applyOnlyIfSmaller,
    isEdit,
    maximumPercentageDecreasePrice,
    maximumPercentageIncreasePrice,
  }: UseBatchPriceCardsPayload) => {
    setIsApiLoading(true);
    const BATCH_SIZE = 250;
    const batches = splitIntoBatches(cardsBoardFiltered, BATCH_SIZE);
    let tempBoard = [...cardsBoard];

    for (let i = 0; i < batches.length; i++) {
      const currentBatch = batches[i];
      try {
        const response = await batchPriceCards({
          cards: currentBatch,
          strategyId,
          applyOnlyIfGreater,
          applyOnlyIfSmaller,
          maximumPercentageDecreasePrice,
          maximumPercentageIncreasePrice,
        }).unwrap();

        tempBoard = tempBoard.map((card) => {
          const updated = response.pricedCards.find((c) => c.uid === card.uid);
          return updated
            ? {
                ...card,
                ...(isEdit ? {} : { originalPrice: card.price }),
                price: updated.price,
                hasPriceIssue: updated.hasPriceIssue,
                uploadingError: updated.uploadingError,
                hasBeenEdited: card.price !== updated.price,
                priceTakenInConsiderationCardTrader:
                  updated.pricesTakenInConsideration,
                priceTakenInConsiderationCardMarket: undefined,
                warningDetailCard: updated.warningDetailCard,
              }
            : card;
        });

        console.log(`✅ Batch ${i + 1}/${batches.length} priced`);
      } catch (err) {
        console.error(`❌ Batch ${i + 1} failed`, err);
        const failedUids = new Set(currentBatch.map((c) => c.uid));

        tempBoard = tempBoard.map((card) =>
          failedUids.has(card.uid)
            ? {
                ...card,
                hasPriceIssue: true,
                uploadingError: "Pricing API error",
                priceTakenInConsiderationCardTrader: undefined,
                priceTakenInConsiderationCardMarket: undefined,
              }
            : card
        );
      }
    }

    onUpdateBoard(tempBoard);
    setIsApiLoading(false);
  };

  return { priceCards, isApiLoading };
};

export const useBatchPriceCardsCardMarket = (): UseBatchPriceCardsResult => {
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [batchPriceCards] = useBatchPriceCardsCardMarketMutation();
  const game = useSelector(selectGame);

  const splitIntoBatches = (
    array: CardSelected[],
    size: number
  ): BatchPriceCardsRequestType[][] => {
    const batches = [];
    for (let i = 0; i < array.length; i += size) {
      const batch = array.slice(i, i + size).map((card) => ({
        price: card.price ?? 0,
        blue_print_id: card.id,
        condition: card?.condition,
        is_foil: card.finish === "foil" || false,
        language: card.language,
        is_signed: card?.signed || false,
        is_altered: false,
        uid: card.uid,
        game: game.card_trader_game,
        is_first_edition: card?.isFirstEdition ?? false,
        is_reverse: card?.isReversed ?? false,
      }));
      batches.push(batch);
    }
    return batches;
  };

  const priceCards = async ({
    cardsBoard,
    onUpdateBoard,
    cardsBoardFiltered,
    strategyId,
    applyOnlyIfGreater,
    applyOnlyIfSmaller,
    isEdit,
    maximumPercentageDecreasePrice,
    maximumPercentageIncreasePrice,
  }: UseBatchPriceCardsPayload) => {
    setIsApiLoading(true);
    const BATCH_SIZE = 250;
    const batches = splitIntoBatches(cardsBoardFiltered, BATCH_SIZE);
    let tempBoard = [...cardsBoard];

    for (let i = 0; i < batches.length; i++) {
      const currentBatch = batches[i];
      try {
        const response = await batchPriceCards({
          cards: currentBatch,
          strategyId,
          applyOnlyIfGreater,
          applyOnlyIfSmaller,
          maximumPercentageDecreasePrice,
          maximumPercentageIncreasePrice,
        }).unwrap();

        tempBoard = tempBoard.map((card) => {
          const updated = response.pricedCards.find((c) => c.uid === card.uid);
          return updated
            ? {
                ...card,
                ...(isEdit ? {} : { originalPrice: card.price }),
                price: updated.price,
                hasPriceIssue: updated.hasPriceIssue,
                uploadingError: updated.uploadingError,
                hasBeenEdited: card.price !== updated.price,
                priceTakenInConsiderationCardTrader: undefined,
                priceTakenInConsiderationCardMarket:
                  updated.pricesTakenInConsideration,
                warningDetailCard: updated.warningDetailCard,
              }
            : card;
        });

        console.log(`✅ Batch ${i + 1}/${batches.length} priced`);
      } catch (err) {
        console.error(`❌ Batch ${i + 1} failed`, err);
        const failedUids = new Set(currentBatch.map((c) => c.uid));

        tempBoard = tempBoard.map((card) =>
          failedUids.has(card.uid)
            ? {
                ...card,
                hasPriceIssue: true,
                uploadingError: "Pricing API error",
                priceTakenInConsiderationCardTrader: undefined,
                priceTakenInConsiderationCardMarket: undefined,
              }
            : card
        );
      }
    }

    onUpdateBoard(tempBoard);
    setIsApiLoading(false);
  };

  return { priceCards, isApiLoading };
};

export const useBatchPriceCardsCardMarketTrendPrice =
  (): UseBatchPriceCardsResultTrend => {
    const [isApiLoading, setIsApiLoading] = useState(false);
    const [batchPriceCardsTrend] = useBatchPriceCardsCardMarketTrendMutation();
    const game = useSelector(selectGame);

    const splitIntoBatches = (
      array: CardSelected[],
      size: number
    ): BatchPriceCardsRequestType[][] => {
      const batches = [];
      for (let i = 0; i < array.length; i += size) {
        const batch = array.slice(i, i + size).map((card) => ({
          price: card.price ?? 0,
          blue_print_id: card.id,
          condition: card?.condition,
          is_foil: card.finish === "foil" || false,
          language: card.language,
          is_signed: card?.signed || false,
          is_altered: false,
          uid: card.uid,
          game: game.card_trader_game,
          is_first_edition: card?.isFirstEdition ?? false,
          is_reverse: card?.isReversed ?? false,
        }));
        batches.push(batch);
      }
      return batches;
    };

    const priceCards = async ({
      cardsBoard,
      onUpdateBoard,
      cardsBoardFiltered,
      selectedAvgStrategy,
      percentage,
      applyOnlyIfGreater,
      applyOnlyIfSmaller,
      minPrice,
      isEdit,
      maximumPercentageDecreasePrice,
      maximumPercentageIncreasePrice,
    }: UseBatchPriceCardsPayloadTrend) => {
      setIsApiLoading(true);
      const BATCH_SIZE = 250;
      const batches = splitIntoBatches(cardsBoardFiltered, BATCH_SIZE);
      let tempBoard = [...cardsBoard];

      for (let i = 0; i < batches.length; i++) {
        const currentBatch = batches[i];
        try {
          const response = await batchPriceCardsTrend({
            cards: currentBatch,
            avgStrategy: selectedAvgStrategy,
            percentage,
            applyOnlyIfGreater,
            applyOnlyIfSmaller,
            minPrice,
            maximumPercentageDecreasePrice,
            maximumPercentageIncreasePrice,
          }).unwrap();

          tempBoard = tempBoard.map((card) => {
            const updated = response.pricedCards.find(
              (c) => c.uid === card.uid
            );
            return updated
              ? {
                  ...card,
                  ...(isEdit ? {} : { originalPrice: card.price }),
                  price: updated.price,
                  hasPriceIssue: updated.hasPriceIssue,
                  uploadingError: updated.uploadingError,
                  hasBeenEdited: card.price !== updated.price,
                  priceTakenInConsiderationCardTrader: undefined,
                  priceTakenInConsiderationCardMarket: undefined,
                  warningDetailCard: updated.warningDetailCard,
                }
              : card;
          });

          console.log(`✅ Batch ${i + 1}/${batches.length} priced`);
        } catch (err) {
          console.error(`❌ Batch ${i + 1} failed`, err);
          const failedUids = new Set(currentBatch.map((c) => c.uid));

          tempBoard = tempBoard.map((card) =>
            failedUids.has(card.uid)
              ? {
                  ...card,
                  hasPriceIssue: true,
                  uploadingError: "Pricing API error",
                  priceTakenInConsiderationCardTrader: undefined,
                  priceTakenInConsiderationCardMarket: undefined,
                }
              : card
          );
        }
      }

      onUpdateBoard(tempBoard);
      setIsApiLoading(false);
    };

    return { priceCards, isApiLoading };
  };
